import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './topbar.css';
import { useAuth } from '../Constant/Auth';
import { useAdminLibrary } from '../Admin/AdminLibraryContext';
import { useActiveLibraryId } from '../Constant/useActiveLibrary';
import { getNotificationsByLibrary } from '../api/api';
import SearchableSelect from '../shared/SearchableSelect';

const typeIcon = (type) => {
  switch (String(type).toLowerCase()) {
    case 'fine':        return 'fas fa-rupee-sign';
    case 'overdue':     return 'fas fa-exclamation-triangle';
    case 'due_date':    return 'fas fa-clock';
    case 'issue':       return 'fas fa-book-open';
    case 'return':      return 'fas fa-undo-alt';
    case 'reservation': return 'fas fa-bookmark';
    default:            return 'fas fa-bell';
  }
};

const typeColor = (type) => {
  switch (String(type).toLowerCase()) {
    case 'fine':        return '#dc2626';
    case 'overdue':     return '#d97706';
    case 'due_date':    return '#2563eb';
    case 'issue':       return '#16a34a';
    case 'return':      return '#0891b2';
    case 'reservation': return '#7c3aed';
    default:            return '#64748b';
  }
};

const timeAgo = (dateStr) => {
  if (!dateStr) return '';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1)   return 'just now';
  if (mins < 60)  return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24)   return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
};

const TopBar = ({ onHamburger = () => {} }) => {
  const { logout, user } = useAuth();
  const admin = useAdminLibrary();
  const navigate = useNavigate();

  const libraryId = useActiveLibraryId() || user?.libraryId;

  const [notifications, setNotifications] = useState([]);
  const [open, setOpen]                   = useState(false);
  const [loading, setLoading]             = useState(false);
  const [readIds, setReadIds]             = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem('nb_read') || '[]')); }
    catch { return new Set(); }
  });

  const dropdownRef = useRef(null);

  const fetchNotifications = async () => {
    if (!libraryId) return;
    setLoading(true);
    try {
      const data = await getNotificationsByLibrary(libraryId);
      const sorted = (data || []).sort(
        (a, b) => new Date(b.created_at) - new Date(a.created_at)
      );
      setNotifications(sorted);
    } catch {
      /* silent — bell is non-critical */
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 60000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [libraryId]);

  /* Close dropdown on outside click */
  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleBellClick = () => {
    const next = !open;
    setOpen(next);
    if (next) {
      /* Mark all currently visible as read */
      const newRead = new Set(readIds);
      notifications.forEach((n) => newRead.add(n.id));
      setReadIds(newRead);
      localStorage.setItem('nb_read', JSON.stringify([...newRead]));
    }
  };

  const unreadCount = notifications.filter((n) => !readIds.has(n.id)).length;

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div className="topbarnew">
      <div className="topbar-inner">

        <button
          className="lib-hamburger"
          onClick={onHamburger}
          aria-label="Open navigation menu"
          title="Menu"
        >
          <i className="fas fa-bars" />
        </button>

        <span className="app-name">Community Library Management System</span>

        <div className="topbar-actions">

            {/* Super Admin: library selector. Drives the "active library" that
                every reused librarian page reads. */}
            {admin?.isAdmin && (
              <div className="admin-lib-picker" title="Select library">
                <i className="fas fa-school admin-lib-picker__icon" />
                <SearchableSelect
                  value={admin.selectedLibraryId ?? ''}
                  onChange={(e) => admin.setSelectedLibraryId(e.target.value)}
                  options={admin.libraries.map((lib) => ({
                    value: lib.id,
                    label: lib.library_name || lib.name || `Library #${lib.id}`
                  }))}
                  loading={admin.loading}
                  placeholder={admin.loading ? 'Loading…' : 'No libraries'}
                  clearable={false}
                />
              </div>
            )}

            {/* Notification Bell */}
            {libraryId && (
              <div className="nb-wrap" ref={dropdownRef}>
                <button className="nb-bell" onClick={handleBellClick} title="Notifications">
                  <i className="fas fa-bell" />
                  {unreadCount > 0 && (
                    <span className="nb-badge">{unreadCount > 99 ? '99+' : unreadCount}</span>
                  )}
                </button>

                {open && (
                  <div className="nb-dropdown">
                    <div className="nb-header">
                      <span><i className="fas fa-bell me-2" />Notifications</span>
                      <button
                        className="nb-refresh"
                        onClick={(e) => { e.stopPropagation(); fetchNotifications(); }}
                        title="Refresh"
                      >
                        <i className={`fas fa-sync-alt ${loading ? 'fa-spin' : ''}`} />
                      </button>
                    </div>

                    <div className="nb-list">
                      {loading && notifications.length === 0 ? (
                        <div className="nb-empty">
                          <i className="fas fa-spinner fa-spin" />
                          <span>Loading…</span>
                        </div>
                      ) : notifications.length === 0 ? (
                        <div className="nb-empty">
                          <i className="fas fa-bell-slash" />
                          <span>No notifications yet</span>
                        </div>
                      ) : (
                        notifications.slice(0, 20).map((n) => {
                          const isUnread = !readIds.has(n.id);
                          return (
                            <div
                              key={n.id}
                              className={`nb-item ${isUnread ? 'nb-item--unread' : ''}`}
                            >
                              <div
                                className="nb-icon"
                                style={{ background: typeColor(n.notification_type) + '18', color: typeColor(n.notification_type) }}
                              >
                                <i className={typeIcon(n.notification_type)} />
                              </div>
                              <div className="nb-content">
                                <div className="nb-title">
                                  {n.title || n.notification_type || 'Notification'}
                                  {isUnread && <span className="nb-dot" />}
                                </div>
                                {n.message && (
                                  <div className="nb-message">{n.message}</div>
                                )}
                                <div className="nb-meta">
                                  <span
                                    className="nb-type-badge"
                                    style={{ background: typeColor(n.notification_type) + '18', color: typeColor(n.notification_type) }}
                                  >
                                    {String(n.notification_type || 'general').replace('_', ' ')}
                                  </span>
                                  <span className="nb-time">{timeAgo(n.created_at)}</span>
                                </div>
                              </div>
                            </div>
                          );
                        })
                      )}
                    </div>

                    {notifications.length > 20 && (
                      <div className="nb-footer">
                        Showing 20 of {notifications.length} notifications
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Logout */}
            <span className="newbtn" onClick={handleLogout}>Logout</span>
        </div>

      </div>
    </div>
  );
};

export default TopBar;
